import { useState, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import ProjectForm from './ProjectForm';
import ProjectDetail from './ProjectDetail';
import api, { stripDocFiles } from './api';

// ─── Document upload helpers ─────────────────────────────────────────────────

const docTreeHasPending = folders => (folders || []).some(f =>
  (f.files || []).some(x => x._file) || docTreeHasPending(f.subfolders)
);

// Завантажує всі pending-файли дерева на сервер, повертає чисте дерево з serverId
async function uploadPendingDocs(projectId, folders) {
  const out = [];
  for (const f of (folders || [])) {
    const files = [];
    for (const file of (f.files || [])) {
      if (file._file) {
        try {
          const res = await api.projects.uploadFiles(projectId, [file._file]);
          const meta = res.files?.[0];
          const { _file, ...rest } = file;
          files.push(meta ? { ...rest, serverId: meta.id } : rest);
        } catch (err) {
          console.warn('Doc upload failed:', file.name, err);
          const { _file, ...rest } = file;
          files.push(rest);
        }
      } else {
        files.push(file);
      }
    }
    out.push({ ...f, files, subfolders: await uploadPendingDocs(projectId, f.subfolders) });
  }
  return out;
}

const statusConfig = {
  'Planning':   { color: 'var(--info-text)', bg: 'var(--info-bg)' },
  'Confirmed':  { color: 'var(--purple-text)', bg: 'var(--purple-bg)' },
  'Active':     { color: 'var(--success-text)', bg: 'var(--success-bg)' },
  'On Hold':    { color: 'var(--warning-text)', bg: 'var(--warning-bg)' },
  'Completed':  { color: 'var(--text-muted)',    bg: 'var(--bg-subtle)'    },
  'Canceled':   { color: 'var(--accent)', bg: 'var(--danger-bg)' },
};

export const mockProjects = [];

const STATUSES = ['Planning', 'Confirmed', 'Active', 'On Hold', 'Completed', 'Canceled'];
const TYPES    = ['Crewing', 'Dry hire', 'Permanent installation'];

// Payment Status — окремий вимір від основного статусу проєкту (стадія
// проєкту vs стан оплати), тому множинний вибір: проєкт може бути одночасно
// Active і Waiting for Payment. Зберігається в financeExtra.paymentStatus,
// міграція БД не потрібна (той самий JSONB-патерн, що й persons/assigned/...).
export const PAYMENT_STATUSES = ['Waiting for Payment', 'Payment for Supplier', 'Equipment Checking'];
const paymentStatusConfig = {
  'Waiting for Payment':  { color: 'var(--warning-text)', bg: 'var(--warning-bg)' },
  'Payment for Supplier': { color: 'var(--purple-text)', bg: 'var(--purple-bg)' },
  'Equipment Checking':   { color: 'var(--info-text)', bg: 'var(--info-bg)' },
};
export function PaymentStatusBadges({ statuses = [], style = {} }) {
  if (!statuses || statuses.length === 0) return null;
  return (
    <>
      {statuses.map(s => {
        const cfg = paymentStatusConfig[s] || { color: 'var(--text-muted)', bg: 'var(--bg-subtle)' };
        return (
          <span key={s} style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}`, borderRadius: '4px', padding: '2px 8px', fontSize: '10px', fontWeight: 'bold', whiteSpace: 'nowrap', marginLeft: '6px', ...style }}>
            {s}
          </span>
        );
      })}
    </>
  );
}

// ─── Currency & date helpers ─────────────────────────────────────────────────
// Курси відносно EUR (1 EUR = rate CUR). Приблизні — для Grand Total.
export const CURRENCY_META = {
  EUR: { symbol: '€',    rate: 1 },
  USD: { symbol: '$',    rate: 1.08 },
  GBP: { symbol: '£',    rate: 0.85 },
  UAH: { symbol: '₴',    rate: 48 },
  AED: { symbol: 'AED ', rate: 3.97 },
  SAR: { symbol: 'SAR ', rate: 4.05 },
  KWD: { symbol: 'KWD ', rate: 0.33 },
  QAR: { symbol: 'QAR ', rate: 3.93 },
};
export const curOf = p => p.currency || 'EUR';

// Assigned може містити рядки (legacy), фрілансерів {id,name,...} і адмінів {userId,name,isAdmin}
const assignedToNames = p => (p.assigned || []).map(a => typeof a === 'string' ? a : (a?.name || ''));
export const fmtMoney = (val, cur) =>
  `${CURRENCY_META[cur]?.symbol ?? `${cur} `}${Number(val || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

// '2026-07-10' → '10 Jul 2026' (без часу)
const fmtDate = d => {
  if (!d) return '—';
  const dt = new Date(String(d).slice(0, 10) + 'T00:00:00');
  return isNaN(dt) ? String(d) : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ALL_COLUMNS = [
  { key: 'name',      label: 'Project',  sortable: true },
  { key: 'company',   label: 'Company',  sortable: true },
  { key: 'type',      label: 'Type',     sortable: true },
  { key: 'location',  label: 'Location', sortable: true },
  { key: 'dates',     label: 'Dates',    sortable: true },
  { key: 'status',    label: 'Status',   sortable: true },
  { key: 'assigned',  label: 'Assigned', sortable: true },
  { key: 'income',    label: 'Income',   sortable: true },
  { key: 'outcome',   label: 'Outcome',  sortable: true },
  { key: 'profit',    label: 'Profit',   sortable: true },
];

const getSortValue = (p, key) => {
  switch (key) {
    case 'dates':    return p.startDate || '';
    case 'assigned': return p.assigned.length;
    case 'status':   return STATUSES.indexOf(p.status);
    case 'income':
    case 'outcome':
    case 'profit':   return p[key] ?? 0;
    default:         return (p[key] || '').toString().toLowerCase();
  }
};

const DEFAULT_VISIBLE = new Set([
  'name', 'company', 'type', 'location', 'dates', 'status', 'assigned', 'profit',
]);

// ─── Export helpers ────────────────────────────────────────────────────────────

const EXPORT_HEADERS = ['Project', 'Company', 'Type', 'Location', 'Start Date', 'End Date', 'Status', 'Assigned', 'Income', 'Outcome', 'Profit'];
const getExportRow = p => [p.name, p.company, p.type, p.location, p.startDate, p.endDate, p.status, assignedToNames(p).join(', '), p.income, p.outcome, p.profit];

function exportCSV(projects) {
  const escape = v => `"${String(v).replace(/"/g, '""')}"`;
  const rows   = [EXPORT_HEADERS, ...projects.map(getExportRow)];
  const csv    = rows.map(r => r.map(escape).join(',')).join('\n');
  const blob   = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url    = URL.createObjectURL(blob);
  const a      = document.createElement('a');
  a.href = url; a.download = `projects_${new Date().toISOString().slice(0,10)}.csv`; a.click();
  URL.revokeObjectURL(url);
}

function exportExcel(projects) {
  const tr = (cells, tag = 'td') => `<tr>${cells.map(c => `<${tag}>${c}</${tag}>`).join('')}</tr>`;
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="UTF-8"></head><body><table>${tr(EXPORT_HEADERS,'th')}${projects.map(p => tr(getExportRow(p))).join('')}</table></body></html>`;
  const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `projects_${new Date().toISOString().slice(0,10)}.xls`; a.click();
  URL.revokeObjectURL(url);
}

// ─── Components ───────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = statusConfig[status] || { color: 'var(--text-muted)', bg: 'var(--bg-subtle)' };
  return (
    <span style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}`, borderRadius: '4px', padding: '2px 10px', fontSize: '11px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
      {status}
    </span>
  );
}

function SortIcon({ col, sortKey, sortDir }) {
  if (!col.sortable) return null;
  if (col.key !== sortKey) return <span style={{ color: 'var(--border-strong)', marginLeft: '4px' }}>↕</span>;
  return <span style={{ color: 'var(--accent)', marginLeft: '4px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
}

// ─── Quick Status Cell ────────────────────────────────────────────────────────

function QuickStatusCell({ project, onStatusChange }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState({ top: 0, left: 0 });
  const triggerRef      = useRef(null);

  const handleOpen = e => {
    e.stopPropagation();
    if (!open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left });
    }
    setOpen(p => !p);
  };

  return (
    <div style={{ display: 'inline-block' }}>
      <div
        ref={triggerRef}
        onClick={handleOpen}
        style={{ cursor: 'pointer', userSelect: 'none' }}
        title="Click to change status">
        <StatusBadge status={project.status} />
        <PaymentStatusBadges statuses={project.paymentStatus} />
        <span style={{ color: 'var(--text-disabled)', fontSize: '9px', marginLeft: '3px' }}>▾</span>
      </div>
      {open && ReactDOM.createPortal(
        <>
          <div
            onClick={e => { e.stopPropagation(); setOpen(false); }}
            style={{ position: 'fixed', inset: 0, zIndex: 990 }}
          />
          <div style={{
            position: 'fixed', top: pos.top, left: pos.left,
            background: 'var(--bg-menu)', border: '1px solid var(--border-strong)', borderRadius: '8px',
            padding: '4px', zIndex: 1000, boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
            minWidth: '160px',
          }}>
            {STATUSES.map(s => (
              <div
                key={s}
                onClick={e => { e.stopPropagation(); onStatusChange(project.id, s); setOpen(false); }}
                style={{ padding: '6px 10px', cursor: 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <StatusBadge status={s} />
                {s === project.status && <span style={{ color: 'var(--text-dim)', fontSize: '10px', marginLeft: '6px' }}>✓</span>}
              </div>
            ))}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

// ─── Row Actions Menu (⋮) ─────────────────────────────────────────────────────

function RowActionsMenu({ project, onEdit, onClone, onArchive, onRestore, onDelete }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState({ top: 0, left: 0 });
  const btnRef          = useRef(null);

  const handleOpen = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.right });
    }
    setOpen(p => !p);
  };

  const item = (label, icon, color, onClick) => (
    <div
      onClick={e => { e.stopPropagation(); onClick(); setOpen(false); }}
      style={{ padding: '9px 14px', cursor: 'pointer', borderRadius: '4px', color: color || 'var(--text-body)', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <span>{icon}</span> {label}
    </div>
  );

  return (
    <div style={{ display: 'inline-block' }} onClick={e => e.stopPropagation()}>
      <button
        ref={btnRef}
        onClick={handleOpen}
        style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-dim)', padding: '4px 8px', borderRadius: '6px', fontSize: '14px', cursor: 'pointer', lineHeight: 1 }}
        title="More actions">
        ⋮
      </button>
      {open && ReactDOM.createPortal(
        <>
          <div
            onClick={e => { e.stopPropagation(); setOpen(false); }}
            style={{ position: 'fixed', inset: 0, zIndex: 990 }}
          />
          <div style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            transform: 'translateX(-100%)',
            background: 'var(--bg-menu)', border: '1px solid var(--border-strong)', borderRadius: '8px',
            padding: '4px', zIndex: 1000, boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
            minWidth: '170px',
          }}>
            {!project.archived && item('Edit', '✏️', 'var(--text-body)', onEdit)}
            {item('Clone', '⧉', 'var(--text-body)', onClone)}
            <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }} />
            {project.archived
              ? item('Restore', '↩', 'var(--success-text)', onRestore)
              : item('Archive', '📦', 'var(--warning-text)', onArchive)
            }
            {project.archived && (
              <>
                <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }} />
                {item('Delete permanently', '🗑', 'var(--accent)', onDelete)}
              </>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

// ─── Delete Confirmation Modal ─────────────────────────────────────────────────

function DeleteModal({ project, onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: '12px', padding: '32px', maxWidth: '420px', width: '90%', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ fontSize: '32px', textAlign: 'center', marginBottom: '16px' }}>🗑</div>
        <h3 style={{ color: 'var(--text-primary)', textAlign: 'center', margin: '0 0 8px 0', fontSize: '18px' }}>Delete permanently?</h3>
        <p style={{ color: 'var(--text-muted)', textAlign: 'center', fontSize: '13px', margin: '0 0 16px 0', lineHeight: '1.5' }}>
          This will permanently delete <span style={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>"{project.name}"</span>.<br />
          This action cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={onCancel}
            style={{ flex: 1, background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '10px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{ flex: 1, background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '10px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Side Panel ───────────────────────────────────────────────────────────────

function SidePanel({ project, onClose, onEdit, onDetails, onArchive, onRestore, onClone, onDelete }) {
  if (!project) return null;
  const cfg = statusConfig[project.status] || { color: 'var(--text-muted)', bg: 'var(--bg-subtle)' };

  const row = (label, value, color) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--bg-subtle)' }}>
      <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>{label}</span>
      <span style={{ color: color || 'var(--text-body)', fontSize: '13px', fontWeight: color ? 'bold' : 'normal', textAlign: 'right', maxWidth: '60%' }}>{value || '—'}</span>
    </div>
  );

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 400 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: '400px', background: 'var(--bg-panel)',
        borderLeft: '1px solid var(--border)',
        zIndex: 500, display: 'flex', flexDirection: 'column',
        fontFamily: 'Arial, sans-serif',
        boxShadow: '-8px 0 32px rgba(0,0,0,0.5)',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <StatusBadge status={project.status} />
              <PaymentStatusBadges statuses={project.paymentStatus} style={{ marginLeft: 0 }} />
              {project.archived && (
                <span style={{ background: 'var(--border)', color: 'var(--text-dim)', border: '1px solid var(--border-strong)', borderRadius: '4px', padding: '2px 8px', fontSize: '10px', fontWeight: 'bold' }}>
                  ARCHIVED
                </span>
              )}
            </div>
            <span onClick={onClose} style={{ color: 'var(--text-dim)', fontSize: '18px', cursor: 'pointer', lineHeight: 1 }}>✕</span>
          </div>
          <h2 style={{ color: 'var(--text-primary)', margin: '0 0 4px 0', fontSize: '18px', fontWeight: 'bold' }}>{project.name}</h2>
          <p style={{ color: 'var(--text-faint)', margin: 0, fontSize: '13px' }}>{project.company}</p>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 24px' }}>
          <p style={{ color: 'var(--text-disabled)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', margin: '16px 0 4px 0' }}>General</p>
          {row('Type',       project.type)}
          {row('Location',   project.location)}
          {row('Start Date', fmtDate(project.startDate))}
          {row('End Date',   fmtDate(project.endDate))}

          <p style={{ color: 'var(--text-disabled)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', margin: '16px 0 4px 0' }}>Crew</p>
          <div style={{ padding: '10px 0', borderBottom: '1px solid var(--bg-subtle)' }}>
            <span style={{ color: 'var(--text-dim)', fontSize: '12px', display: 'block', marginBottom: '8px' }}>Assigned</span>
            {(project.assigned || []).length ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {/* dedupe: той самий фрілансер може мати кілька записів (проміжки участі з перервами) — тут лише зведений список, без дат */}
                {Array.from(new Map((project.assigned || []).map(a => {
                  const isAdmin = typeof a === 'object' && a?.isAdmin;
                  const name = typeof a === 'string' ? a : (a?.name || '');
                  return [`${isAdmin}-${name}`, { isAdmin, name }];
                })).values()).map((a, i) => (
                  <span key={i} style={{ background: a.isAdmin ? 'var(--info-bg)' : 'var(--bg-subtle)', border: `1px solid ${a.isAdmin ? 'var(--info-text)' : 'var(--border-strong)'}`, color: a.isAdmin ? 'var(--info-text)' : 'var(--text-body)', borderRadius: '20px', padding: '3px 10px', fontSize: '12px' }}>
                    {a.isAdmin ? '🛡' : '👤'} {a.name}
                  </span>
                ))}
              </div>
            ) : <span style={{ color: 'var(--text-disabled)', fontSize: '13px' }}>No crew assigned</span>}
          </div>

          <p style={{ color: 'var(--text-disabled)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', margin: '16px 0 4px 0' }}>Finance</p>
          {row('Currency', curOf(project))}
          {row('Income',  project.income  ? fmtMoney(project.income, curOf(project))  : '—', 'var(--success-text)')}
          {row('Outcome', project.outcome ? fmtMoney(project.outcome, curOf(project)) : '—', 'var(--accent)')}
          {row('Profit',  (project.income || project.outcome) ? fmtMoney(project.profit ?? (project.income - project.outcome), curOf(project)) : '—', (project.profit ?? 0) >= 0 ? 'var(--success-text)' : 'var(--accent)')}

          {project.description && (
            <>
              <p style={{ color: 'var(--text-disabled)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', margin: '16px 0 8px 0' }}>Description</p>
              <p style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: '1.6', margin: 0 }}>{project.description}</p>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* View Details — only for non-archived */}
          {!project.archived && (
            <button
              onClick={onDetails}
              style={{ width: '100%', background: 'var(--bg-card)', border: '1px solid var(--accent)', color: 'var(--accent)', padding: '10px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
              View Full Details →
            </button>
          )}

          {/* Edit + Clone */}
          {!project.archived && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={onEdit}
                style={{ flex: 1, background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '10px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
                ✏️ Edit
              </button>
              <button
                onClick={onClone}
                style={{ flex: 1, background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '10px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                ⧉ Clone
              </button>
            </div>
          )}

          {/* Archive / Restore */}
          {project.archived ? (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={onRestore}
                style={{ flex: 1, background: 'transparent', border: '1px solid var(--success-text)', color: 'var(--success-text)', padding: '10px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                ↩ Restore
              </button>
              <button
                onClick={onClone}
                style={{ flex: 1, background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '10px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                ⧉ Clone
              </button>
            </div>
          ) : (
            <button
              onClick={onArchive}
              style={{ width: '100%', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-dim)', padding: '8px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer' }}>
              📦 Archive project
            </button>
          )}

          {/* Delete permanently — only from archive */}
          {project.archived && (
            <button
              onClick={onDelete}
              style={{ width: '100%', background: 'transparent', border: '1px solid var(--accent)', color: 'var(--accent)', padding: '8px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer' }}>
              🗑 Delete permanently
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────

function ProjectsPage({ onBack, projects, setProjects, companies = [], freelancers = [], equipment = [], adminUsers = [], allUsers = [], currentUser = null }) {
  const [activeTab, setActiveTab]             = useState('active'); // 'active' | 'archived'

  const [view, setView]                       = useState('list');
  const [search, setSearch]                   = useState('');
  const [filterStatus, setFilterStatus]       = useState('');
  const [filterType, setFilterType]           = useState('');
  const [page, setPage]                       = useState('list');
  const [selectedProject, setSelectedProject] = useState(null);
  const [visibleCols, setVisibleCols]         = useState(new Set(DEFAULT_VISIBLE));
  const [showColPicker, setShowColPicker]     = useState(false);
  const [showExportMenu, setShowExportMenu]   = useState(false);
  const [sortKey, setSortKey]                 = useState(null);
  const [sortDir, setSortDir]                 = useState('asc');
  const [panelProject, setPanelProject]       = useState(null);
  const [deleteConfirm, setDeleteConfirm]     = useState(null);
  const [grandCur, setGrandCur]               = useState('EUR');

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleSave = async (data) => {
    try {
      const isNew = !data.id || !projects.find(p => p.id === data.id);
      const res = isNew
        ? await api.projects.create(data)
        : await api.projects.update(data.id, data);
      let saved = res.project;

      // Документи: завантажити прикріплені файли і зберегти дерево з serverId
      if (data.docFolders && docTreeHasPending(data.docFolders)) {
        const cleanTree = await uploadPendingDocs(saved.id, data.docFolders);
        try {
          const res2 = await api.projects.update(saved.id, { ...saved, docFolders: cleanTree });
          saved = res2.project;
        } catch (e2) {
          console.warn('Doc tree save error:', e2);
          saved = { ...saved, docFolders: cleanTree };
        }
      }

      setProjects(prev => {
        const exists = prev.find(p => p.id === saved.id);
        if (exists) return prev.map(p => p.id === saved.id ? saved : p);
        return [saved, ...prev];
      });
    } catch (e) {
      console.warn('API save error, falling back to local:', e);
      // Офлайн: файли завантажити нікуди — прибираємо File-обʼєкти
      const fallback = { ...data, docFolders: stripDocFiles(data.docFolders), id: data.id || Date.now() };
      setProjects(prev => {
        const exists = prev.find(p => p.id === fallback.id);
        if (exists) return prev.map(p => p.id === fallback.id ? fallback : p);
        return [fallback, ...prev];
      });
    }
  };

  const handleArchive = async (project) => {
    try { await api.projects.archive(project.id, true); } catch {}
    setProjects(prev => prev.map(p => p.id === project.id ? { ...p, archived: true } : p));
    setPanelProject(null);
  };

  const handleRestore = async (project) => {
    try { await api.projects.archive(project.id, false); } catch {}
    setProjects(prev => prev.map(p => p.id === project.id ? { ...p, archived: false } : p));
    setPanelProject(null);
  };

  const handleDeleteConfirm = (project) => {
    setDeleteConfirm(project);
    setPanelProject(null);
  };

  const handleDeleteExecute = async () => {
    try { await api.projects.delete(deleteConfirm.id); } catch (e) { console.warn('API delete error:', e); }
    setProjects(prev => prev.filter(p => p.id !== deleteConfirm.id));
    setDeleteConfirm(null);
  };

  const handleClone = async (project) => {
    const cloneData = {
      ...project,
      id:       undefined,
      name:     `${project.name} (Copy)`,
      status:   'Planning',
      archived: false,
    };
    try {
      const res = await api.projects.create(cloneData);
      setProjects(prev => [res.project, ...prev]);
    } catch (e) {
      console.warn('API clone error, falling back to local:', e);
      setProjects(prev => [{ ...cloneData, id: Date.now() }, ...prev]);
    }
    setPanelProject(null);
  };

  const handleQuickStatus = async (id, newStatus) => {
    try { await api.projects.setStatus(id, newStatus); } catch {}
    setProjects(prev => prev.map(p => p.id === id ? { ...p, status: newStatus } : p));
  };

  // ─── Derived ────────────────────────────────────────────────────────────────

  const toggleCol = key => setVisibleCols(prev => {
    const n = new Set(prev);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });

  const handleSort = key => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const columns = ALL_COLUMNS.filter(c => visibleCols.has(c.key));

  // ── Column filters ─────────────────────────────────────────────────────────
  const [colFilters, setColFilters] = useState({ name: '', company: '', type: '', location: '', status: '', assigned: '' });
  const setColFilter = (k, v) => setColFilters(prev => ({ ...prev, [k]: v }));
  const hasColFilters = Object.values(colFilters).some(Boolean);
  const clearAllFilters = () => { setSearch(''); setFilterStatus(''); setFilterType(''); setColFilters({ name: '', company: '', type: '', location: '', status: '', assigned: '' }); };

  const tabProjects = useMemo(
    () => projects.filter(p => activeTab === 'archived' ? p.archived : !p.archived),
    [projects, activeTab]
  );

  // Значення для селектів фільтрів — з проєктів поточної вкладки
  const distinct = useMemo(() => {
    const companies  = [...new Set(tabProjects.map(p => p.company).filter(Boolean))].sort();
    const locations  = [...new Set(tabProjects.map(p => p.location).filter(Boolean))].sort();
    const assignedNames = [...new Set(tabProjects.flatMap(p => (p.assigned || []).map(a => typeof a === 'string' ? a : a.name)).filter(Boolean))].sort();
    return { companies, locations, assignedNames };
  }, [tabProjects]);

  const filtered = useMemo(() => {
    const f = tabProjects.filter(p => {
      const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.company.toLowerCase().includes(search.toLowerCase());
      const matchStatus = !filterStatus || p.status === filterStatus;
      const matchType   = !filterType   || p.type   === filterType;
      const cf = colFilters;
      const names = (p.assigned || []).map(a => typeof a === 'string' ? a : a.name);
      const matchCols =
        (!cf.name     || p.name.toLowerCase().includes(cf.name.toLowerCase())) &&
        (!cf.company  || p.company === cf.company) &&
        (!cf.type     || p.type === cf.type) &&
        (!cf.location || p.location === cf.location) &&
        (!cf.status   || p.status === cf.status) &&
        (!cf.assigned || names.includes(cf.assigned));
      return matchSearch && matchStatus && matchType && matchCols;
    });
    if (!sortKey) return f;
    return [...f].sort((a, b) => {
      const av = getSortValue(a, sortKey);
      const bv = getSortValue(b, sortKey);
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [tabProjects, search, filterStatus, filterType, colFilters, sortKey, sortDir]);

  const archivedCount = projects.filter(p => p.archived).length;
  const activeCount   = projects.filter(p => !p.archived).length;

  // ── Totals: окремо по кожній валюті + Grand Total в обраній валюті ────────
  const totalsByCur = useMemo(() => {
    const m = {};
    filtered.forEach(p => {
      const c = curOf(p);
      if (!m[c]) m[c] = { income: 0, outcome: 0, profit: 0 };
      m[c].income  += p.income  || 0;
      m[c].outcome += p.outcome || 0;
      m[c].profit  += (p.profit ?? ((p.income || 0) - (p.outcome || 0)));
    });
    return m;
  }, [filtered]);

  const grandTotal = useMemo(() => {
    const target = CURRENCY_META[grandCur]?.rate || 1;
    return Object.entries(totalsByCur).reduce((acc, [c, t]) => {
      const rate = CURRENCY_META[c]?.rate || 1;
      const k = target / rate; // c → EUR → grandCur
      acc.income  += t.income  * k;
      acc.outcome += t.outcome * k;
      acc.profit  += t.profit  * k;
      return acc;
    }, { income: 0, outcome: 0, profit: 0 });
  }, [totalsByCur, grandCur]);

  // ─── Routes ─────────────────────────────────────────────────────────────────

  if (page === 'new')
    return <ProjectForm onBack={() => setPage('list')} onSave={handleSave} companies={companies} freelancers={freelancers} equipment={equipment} projects={projects} adminUsers={adminUsers} currentUser={currentUser} />;
  if (page === 'edit')
    return <ProjectForm initialData={selectedProject} onBack={() => setPage('list')} onSave={handleSave} companies={companies} freelancers={freelancers} equipment={equipment} projects={projects} adminUsers={adminUsers} currentUser={currentUser} />;
  if (page === 'detail')
    return (
      <ProjectDetail
        project={selectedProject}
        onBack={() => setPage('list')}
        onEdit={() => setPage('edit')}
        allUsers={allUsers}
      />
    );

  // ─── Styles ─────────────────────────────────────────────────────────────────

  const inputStyle = { background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: '6px', color: 'var(--text-body)', padding: '8px 12px', fontSize: '13px', outline: 'none' };
  const tdStyle    = { padding: '12px 14px', color: 'var(--text-body)', fontSize: '13px', borderBottom: '1px solid var(--bg-hover)', verticalAlign: 'middle' };
  const thBase     = { padding: '10px 14px', textAlign: 'left', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', background: 'var(--bg-card)', userSelect: 'none' };
  const thStyle    = col => ({ ...thBase, color: sortKey === col.key ? 'var(--text-body)' : 'var(--text-dim)', cursor: col.sortable ? 'pointer' : 'default' });

  const renderCell = (p, key) => {
    switch (key) {
      case 'name':     return <span style={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>{p.name}</span>;
      case 'company':  return p.company;
      case 'type':     return <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{p.type}</span>;
      case 'location': return p.location;
      case 'dates':    return <span style={{ whiteSpace: 'nowrap', fontSize: '12px' }}>{fmtDate(p.startDate)} → {fmtDate(p.endDate)}</span>;
      case 'status':   return <QuickStatusCell project={p} onStatusChange={handleQuickStatus} />;
      case 'assigned': {
        // dedupe: той самий фрілансер може мати кілька записів (проміжки участі з перервами)
        const names = [...new Set((p.assigned || []).map(a => typeof a === 'string' ? a : a.name))];
        return names.length ? names.join(', ') : <span style={{ color: 'var(--text-disabled)' }}>—</span>;
      }
      case 'income':   return <span style={{ color: 'var(--success-text)' }}>{p.income ? fmtMoney(p.income, curOf(p)) : '—'}</span>;
      case 'outcome':  return <span style={{ color: 'var(--accent)' }}>{p.outcome ? fmtMoney(p.outcome, curOf(p)) : '—'}</span>;
      case 'profit': {
        const profit = (p.profit ?? ((p.income || 0) - (p.outcome || 0)));
        if (!p.income && !p.outcome) return <span style={{ color: 'var(--text-dim)' }}>—</span>;
        return <span style={{ color: profit > 0 ? 'var(--success-text)' : profit < 0 ? 'var(--accent)' : 'var(--text-dim)', fontWeight: 'bold' }}>{fmtMoney(profit, curOf(p))}</span>;
      }
      default:         return '—';
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      style={{ minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif' }}
      onClick={() => { setShowColPicker(false); setShowExportMenu(false); }}>

      {/* Modals */}
      {deleteConfirm && (
        <DeleteModal
          project={deleteConfirm}
          onConfirm={handleDeleteExecute}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      {/* Side Panel */}
      <SidePanel
        project={panelProject}
        onClose={() => setPanelProject(null)}
        onEdit={() => { setSelectedProject(panelProject); setPanelProject(null); setPage('edit'); }}
        onDetails={() => { setSelectedProject(panelProject); setPanelProject(null); setPage('detail'); }}
        onArchive={() => handleArchive(panelProject)}
        onRestore={() => handleRestore(panelProject)}
        onClone={() => handleClone(panelProject)}
        onDelete={() => handleDeleteConfirm(panelProject)}
      />

      {/* Nav */}
      <div style={{ background: 'var(--bg-card)', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span onClick={onBack} style={{ color: 'var(--text-dim)', fontSize: '13px', cursor: 'pointer' }}>← Dashboard</span>
          <span style={{ color: 'var(--border-strong)' }}>|</span>
          <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '16px', letterSpacing: '1px' }}>Projects</span>
        </div>
        {activeTab === 'active' && (
          <button
            onClick={() => setPage('new')}
            style={{ background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '8px 20px', borderRadius: '6px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
            + New Project
          </button>
        )}
      </div>

      {/* Archive Tabs */}
      <div style={{ borderBottom: '1px solid var(--bg-hover)', background: 'var(--bg-input)', padding: '0 32px', display: 'flex', gap: '0' }}>
        {[
          { key: 'active',   label: 'Active Projects', count: activeCount   },
          { key: 'archived', label: 'Archive',          count: archivedCount },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => { setActiveTab(tab.key); clearAllFilters(); }}
            style={{
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid var(--accent)' : '2px solid transparent',
              color: activeTab === tab.key ? 'var(--text-primary)' : 'var(--text-dim)',
              padding: '12px 20px',
              fontSize: '13px',
              cursor: 'pointer',
              fontWeight: activeTab === tab.key ? 'bold' : 'normal',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}>
            {tab.label}
            <span style={{
              background: activeTab === tab.key ? 'var(--border)' : 'var(--bg-card)',
              color: activeTab === tab.key ? 'var(--text-body)' : 'var(--text-disabled)',
              borderRadius: '10px', padding: '1px 7px', fontSize: '11px',
            }}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      <div style={{ padding: '32px' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap' }}>
          <input
            style={{ ...inputStyle, width: '260px' }}
            placeholder="Search projects or companies..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {view === 'grid' && (
            <>
              <select style={inputStyle} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="">All Statuses</option>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
              <select style={inputStyle} value={filterType} onChange={e => setFilterType(e.target.value)}>
                <option value="">All Types</option>
                {TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </>
          )}
          {(search || filterStatus || filterType || hasColFilters) && (
            <span onClick={clearAllFilters}
              style={{ color: 'var(--accent)', fontSize: '12px', cursor: 'pointer' }}>
              Clear filters
            </span>
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>

            {/* Results count */}
            <span style={{ background: 'var(--bg-card)', color: 'var(--text-dim)', borderRadius: '10px', padding: '4px 10px', fontSize: '12px', border: '1px solid var(--border)' }}>
              {filtered.length} project{filtered.length !== 1 ? 's' : ''}
            </span>

            {/* Export */}
            <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
              <button
                onClick={() => { setShowExportMenu(p => !p); setShowColPicker(false); }}
                style={{ background: showExportMenu ? 'var(--border)' : 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                Export ↓
              </button>
              {showExportMenu && (
                <div style={{ position: 'absolute', right: 0, top: '36px', background: 'var(--bg-menu)', border: '1px solid var(--border-strong)', borderRadius: '8px', overflow: 'hidden', zIndex: 200, minWidth: '150px', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                  <div onClick={() => { exportCSV(filtered); setShowExportMenu(false); }}
                    style={{ padding: '10px 16px', color: 'var(--text-body)', fontSize: '13px', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    📄 Export CSV
                  </div>
                  <div style={{ height: '1px', background: 'var(--border)' }} />
                  <div onClick={() => { exportExcel(filtered); setShowExportMenu(false); }}
                    style={{ padding: '10px 16px', color: 'var(--text-body)', fontSize: '13px', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    📊 Export Excel
                  </div>
                </div>
              )}
            </div>

            {/* Columns picker */}
            {view === 'list' && (
              <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                <button
                  onClick={() => { setShowColPicker(p => !p); setShowExportMenu(false); }}
                  style={{ background: showColPicker ? 'var(--border)' : 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                  Columns
                </button>
                {showColPicker && (
                  <div style={{ position: 'absolute', right: 0, top: '36px', background: 'var(--bg-menu)', border: '1px solid var(--border-strong)', borderRadius: '8px', padding: '12px 16px', zIndex: 200, minWidth: '180px', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                    <div style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' }}>Show / Hide Columns</div>
                    {ALL_COLUMNS.map(col => (
                      <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-body)', fontSize: '13px', padding: '4px 0', cursor: 'pointer' }}>
                        <input type="checkbox" checked={visibleCols.has(col.key)} onChange={() => toggleCol(col.key)} />
                        {col.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* View toggle */}
            <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-card)', borderRadius: '6px', padding: '3px', border: '1px solid var(--border)' }}>
              {[{ key: 'list', icon: '☰' }, { key: 'grid', icon: '⊞' }].map(v => (
                <button key={v.key} onClick={() => setView(v.key)}
                  style={{ background: view === v.key ? 'var(--border-strong)' : 'transparent', border: 'none', color: view === v.key ? 'var(--text-primary)' : 'var(--text-dim)', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}>
                  {v.icon}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Archive notice */}
        {activeTab === 'archived' && (
          <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning-text)', borderRadius: '8px', padding: '10px 16px', marginBottom: '16px', color: 'var(--text-muted)', fontSize: '13px' }}>
            📦 Archived projects are hidden from the main view. Restore a project to make it active again, or delete it permanently.
          </div>
        )}

        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text-disabled)', padding: '60px', fontSize: '14px' }}>
            {activeTab === 'archived' ? 'No archived projects' : 'No projects found'}
          </div>
        ) : view === 'list' ? (

          /* List view */
          <>
            <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {columns.map(col => (
                      <th key={col.key} style={thStyle(col)} onClick={() => col.sortable && handleSort(col.key)}>
                        {col.label}<SortIcon col={col} sortKey={sortKey} sortDir={sortDir} />
                      </th>
                    ))}
                    <th style={{ ...thBase, color: 'var(--text-dim)' }}></th>
                  </tr>
                  {/* Рядок фільтрів: текст/селекти для довідкових колонок, фінансові — лише сортування */}
                  <tr>
                    {columns.map(col => {
                      const fs = { width: '100%', boxSizing: 'border-box', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '5px', color: 'var(--text-secondary)', fontSize: '11px', padding: '4px 6px', outline: 'none' };
                      const cell = (content) => (
                        <th key={col.key} style={{ ...thBase, padding: '6px 10px', background: 'var(--bg-subtle)' }}>{content}</th>
                      );
                      switch (col.key) {
                        case 'name':
                          return cell(<input style={fs} placeholder="Filter..." value={colFilters.name} onChange={e => setColFilter('name', e.target.value)} />);
                        case 'company':
                          return cell(
                            <select style={fs} value={colFilters.company} onChange={e => setColFilter('company', e.target.value)}>
                              <option value="">All</option>
                              {distinct.companies.map(c => <option key={c}>{c}</option>)}
                            </select>
                          );
                        case 'type':
                          return cell(
                            <select style={fs} value={colFilters.type} onChange={e => setColFilter('type', e.target.value)}>
                              <option value="">All</option>
                              {TYPES.map(t => <option key={t}>{t}</option>)}
                            </select>
                          );
                        case 'location':
                          return cell(
                            <select style={fs} value={colFilters.location} onChange={e => setColFilter('location', e.target.value)}>
                              <option value="">All</option>
                              {distinct.locations.map(l => <option key={l}>{l}</option>)}
                            </select>
                          );
                        case 'status':
                          return cell(
                            <select style={fs} value={colFilters.status} onChange={e => setColFilter('status', e.target.value)}>
                              <option value="">All</option>
                              {STATUSES.map(s => <option key={s}>{s}</option>)}
                            </select>
                          );
                        case 'assigned':
                          return cell(
                            <select style={fs} value={colFilters.assigned} onChange={e => setColFilter('assigned', e.target.value)}>
                              <option value="">All</option>
                              {distinct.assignedNames.map(n2 => <option key={n2}>{n2}</option>)}
                            </select>
                          );
                        default:
                          return <th key={col.key} style={{ ...thBase, padding: '6px 10px', background: 'var(--bg-subtle)' }}></th>;
                      }
                    })}
                    <th style={{ ...thBase, padding: '6px 10px', background: 'var(--bg-subtle)' }}>
                      {hasColFilters && (
                        <span onClick={clearAllFilters} style={{ color: 'var(--accent)', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' }}>✕ Clear</span>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => (
                    <tr key={p.id}
                      style={{ cursor: 'pointer', opacity: p.archived ? 0.6 : 1 }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-subtle)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      {columns.map(col => (
                        <td key={col.key} style={tdStyle} onClick={() => setPanelProject(p)}>
                          {renderCell(p, col.key)}
                        </td>
                      ))}
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => setPanelProject(p)}
                          style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-dim)', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', marginRight: '6px' }}>
                          👁
                        </button>
                        {!p.archived && (
                          <button
                            onClick={() => { setSelectedProject(p); setPage('edit'); }}
                            style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', marginRight: '6px' }}>
                            Edit
                          </button>
                        )}
                        <RowActionsMenu
                          project={p}
                          onEdit={() => { setSelectedProject(p); setPage('edit'); }}
                          onClone={() => handleClone(p)}
                          onArchive={() => handleArchive(p)}
                          onRestore={() => handleRestore(p)}
                          onDelete={() => handleDeleteConfirm(p)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals — окремо по кожній валюті */}
            {(visibleCols.has('income') || visibleCols.has('outcome') || visibleCols.has('profit')) && (
              <div style={{ marginTop: '12px', background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px 16px' }}>
                {Object.entries(totalsByCur).map(([c, t]) => (
                  <div key={c} style={{ display: 'flex', gap: '24px', alignItems: 'center', padding: '4px 0' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '12px', fontWeight: 'bold', width: '48px' }}>{c}</span>
                    {visibleCols.has('income')  && <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Income: <span style={{ color: 'var(--success-text)' }}>{fmtMoney(t.income, c)}</span></span>}
                    {visibleCols.has('outcome') && <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Outcome: <span style={{ color: 'var(--accent)' }}>{fmtMoney(t.outcome, c)}</span></span>}
                    {visibleCols.has('profit')  && <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Profit: <span style={{ color: t.profit >= 0 ? 'var(--success-text)' : 'var(--accent)' }}>{fmtMoney(t.profit, c)}</span></span>}
                  </div>
                ))}

                {/* Grand Total — все конвертовано в одну валюту */}
                {Object.keys(totalsByCur).length > 1 && (
                  <div style={{ display: 'flex', gap: '24px', alignItems: 'center', padding: '8px 0 2px', borderTop: '1px solid var(--border)', marginTop: '6px' }}>
                    <span style={{ color: 'var(--text-primary)', fontSize: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      GRAND TOTAL
                      <select
                        value={grandCur}
                        onChange={e => setGrandCur(e.target.value)}
                        style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: '4px', color: 'var(--text-body)', fontSize: '11px', padding: '2px 4px' }}>
                        {Object.keys(CURRENCY_META).map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </span>
                    {visibleCols.has('income')  && <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Income: <span style={{ color: 'var(--success-text)', fontWeight: 'bold' }}>{fmtMoney(grandTotal.income, grandCur)}</span></span>}
                    {visibleCols.has('outcome') && <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Outcome: <span style={{ color: 'var(--accent)', fontWeight: 'bold' }}>{fmtMoney(grandTotal.outcome, grandCur)}</span></span>}
                    {visibleCols.has('profit')  && <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Profit: <span style={{ color: grandTotal.profit >= 0 ? 'var(--success-text)' : 'var(--accent)', fontWeight: 'bold' }}>{fmtMoney(grandTotal.profit, grandCur)}</span></span>}
                    <span style={{ color: 'var(--text-disabled)', fontSize: '10px', marginLeft: 'auto' }}>за приблизним курсом</span>
                  </div>
                )}
              </div>
            )}
          </>

        ) : (

          /* Grid view */
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {filtered.map(p => (
              <div key={p.id}
                style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px', cursor: 'pointer', opacity: p.archived ? 0.65 : 1, position: 'relative' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>

                {/* Card actions */}
                <div
                  onClick={e => e.stopPropagation()}
                  style={{ position: 'absolute', top: '12px', right: '12px', display: 'flex', gap: '4px' }}>
                  <RowActionsMenu
                    project={p}
                    onEdit={() => { setSelectedProject(p); setPage('edit'); }}
                    onClone={() => handleClone(p)}
                    onArchive={() => handleArchive(p)}
                    onRestore={() => handleRestore(p)}
                    onDelete={() => handleDeleteConfirm(p)}
                  />
                </div>

                <div onClick={() => setPanelProject(p)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', paddingRight: '28px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                      <StatusBadge status={p.status} />
                      <PaymentStatusBadges statuses={p.paymentStatus} />
                    </div>
                    <span style={{ color: 'var(--text-dim)', fontSize: '11px' }}>{p.type}</span>
                  </div>
                  <h3 style={{ color: 'var(--text-primary)', fontSize: '15px', margin: '0 0 4px 0' }}>{p.name}</h3>
                  <p style={{ color: 'var(--text-faint)', fontSize: '13px', margin: '0 0 12px 0' }}>{p.company}</p>
                  <div style={{ color: 'var(--text-dim)', fontSize: '12px', marginBottom: '4px' }}>📍 {p.location}</div>
                  <div style={{ color: 'var(--text-dim)', fontSize: '12px', marginBottom: '12px' }}>📅 {fmtDate(p.startDate)} → {fmtDate(p.endDate)}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
                    <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>
                      {(p.assigned || []).length ? `👥 ${(p.assigned || []).length} assigned` : '👥 No crew'}
                    </span>
                    <span style={{ color: p.profit > 0 ? 'var(--success-text)' : 'var(--text-dim)', fontWeight: 'bold', fontSize: '13px' }}>
                      {(p.income || p.outcome) ? fmtMoney(p.profit ?? (p.income - p.outcome), curOf(p)) : '—'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProjectsPage;
